/**
 * Staff-side platform API — auth (password + TOTP), scoped directory,
 * lending decisions, KYC review, payments ops, reconciliation, audit, regions.
 * Shares the token store (and silent refresh) with the customer client.
 */
import { request, requestFile, tokenStore } from "./api";

// Same-origin by default in production (nginx proxies /api to the API service).
// `??` not `||` so an intentional empty value means "same origin" — with `||`
// an empty string fell through to localhost and broke every deployed browser.
const API_BASE =
  import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? "http://localhost:8000" : "");

// Shares the customer client's transport: bearer auth + silent refresh on 401.
const req = request;

export interface StaffUser {
  id: string; role: string; email: string | null; full_name: string | null;
  region_ids: string[]; totp_enabled: boolean;
}

export const staffApi = {
  // ---- Auth ----
  login: async (email: string, password: string) => {
    const data = await req<{ totp_required: boolean; access_token?: string; refresh_token?: string }>(
      "POST", "/api/v1/platform/auth/staff/login", { email, password });
    if (data.access_token && data.refresh_token) tokenStore.set(data.access_token, data.refresh_token);
    return data;
  },
  totpVerify: async (email: string, password: string, code: string) => {
    const data = await req<{ access_token: string; refresh_token: string }>(
      "POST", "/api/v1/platform/auth/staff/totp/verify", { email, password, code });
    tokenStore.set(data.access_token, data.refresh_token);
    return data;
  },
  me: () => req<StaffUser>("GET", "/api/v1/platform/auth/me"),
  logout: async () => {
    const refresh = tokenStore.refresh;
    tokenStore.clear();
    if (refresh) await req("POST", "/api/v1/platform/auth/logout", { refresh_token: refresh }).catch(() => undefined);
  },

  // ---- Directory (region-scoped server-side) ----
  customers: (params = "") => req<{ customers: Record<string, unknown>[] }>("GET", `/api/v1/platform/customers${params}`),
  loans: (params = "") => req<{ loans: Record<string, unknown>[] }>("GET", `/api/v1/platform/loans${params}`),
  applications: (params = "") => req<{ applications: Record<string, unknown>[] }>("GET", `/api/v1/platform/applications${params}`),

  // ---- Decisions ----
  approveApplication: (id: string) => req("POST", `/api/v1/platform/lending/applications/${id}/approve`),
  rejectApplication: (id: string, reason: string) =>
    req("POST", `/api/v1/platform/lending/applications/${id}/reject`, { reason }),
  kycQueue: (status = "pending") => req<{ records: Record<string, unknown>[] }>("GET", `/api/v1/platform/lending/kyc?status=${status}`),
  reviewKyc: (id: string, action: string, notes?: string) =>
    req("POST", `/api/v1/platform/lending/kyc/${id}/review`, { action, notes }),
  kycFileUrl: (recordId: string, index: number) =>
    `${API_BASE}/api/v1/platform/kyc-files/${recordId}/${index}`,

  // ---- Payments ops ----
  disburse: (loanId: string) => req("POST", `/api/v1/platform/payments/loans/${loanId}/disburse`, {}),

  // ---- Reconciliation ----
  reconRuns: () => req<{ runs: Record<string, unknown>[] }>("GET", "/api/v1/platform/reconciliation/runs"),
  reconTrigger: () => req("POST", "/api/v1/platform/reconciliation/runs/trigger"),
  reconItems: (state = "open") => req<{ items: Record<string, unknown>[] }>("GET", `/api/v1/platform/reconciliation/items?state=${state}`),
  resolveItem: (id: string, action: string, note: string) =>
    req("POST", `/api/v1/platform/reconciliation/items/${id}/resolve`, { action, note }),
  unmatched: () => req<{ unmatched: Record<string, unknown>[] }>("GET", "/api/v1/platform/reconciliation/unmatched"),
  resolveUnmatched: (id: string, action: string, note: string) =>
    req("POST", `/api/v1/platform/reconciliation/unmatched/${id}/resolve`, { action, note }),

  disbursements: () => req<Record<string, any>>("GET", "/api/v1/platform/disbursements"),
  retryDisbursement: (loanId: string) =>
    req("POST", `/api/v1/platform/payments/loans/${loanId}/disburse/retry`, {}),
  customerDetail: (id: string) =>
    req<Record<string, any>>("GET", `/api/v1/platform/customers/${id}/detail`),
  loanDetail: (id: string) =>
    req<Record<string, any>>("GET", `/api/v1/platform/loans/${id}/detail`),

  paymentsReceived: (params = "") =>
    req<{ total_amount: number; total_count: number; returned: number; payments: Record<string, any>[] }>(
      "GET", `/api/v1/platform/payments-received${params}`),

  // ---- Collections ----
  collectionBuckets: () => req<{
    buckets: { label: string; count: number; amount: number }[];
    due_today: number; total_at_risk: number;
  }>("GET", "/api/v1/platform/collections/buckets"),
  collectionQueue: (params = "") =>
    req<{ cases: Record<string, any>[]; count: number }>(
      "GET", `/api/v1/platform/collections/queue${params}`),
  collectionCase: (id: string) =>
    req<Record<string, any>>("GET", `/api/v1/platform/collections/cases/${id}`),
  logInteraction: (id: string, body: Record<string, unknown>) =>
    req("POST", `/api/v1/platform/collections/cases/${id}/interactions`, body),
  takePromise: (id: string, body: Record<string, unknown>) =>
    req("POST", `/api/v1/platform/collections/cases/${id}/promises`, body),
  assignCase: (id: string, assigned_to: string | null, reason?: string) =>
    req("POST", `/api/v1/platform/collections/cases/${id}/assign`, { assigned_to, reason }),
  collectionsOversight: () =>
    req<{ collectors: Record<string, any>[]; unassigned: { count: number; value_at_risk: number } }>(
      "GET", "/api/v1/platform/collections/oversight"),
  runClassification: () => req("POST", "/api/v1/platform/collections/classify"),

  // ---- Executive analytics ----
  executive: (params = "") =>
    req<Record<string, any>>("GET", `/api/v1/platform/analytics/executive${params}`),
  salesPerformance: (period?: string) =>
    req<{ period: string; regions: Record<string, any>[] }>(
      "GET", `/api/v1/platform/analytics/sales${period ? `?period=${period}` : ""}`),
  setTarget: (body: Record<string, unknown>) =>
    req("POST", "/api/v1/platform/analytics/targets", body),
  runSnapshots: () => req("POST", "/api/v1/platform/analytics/snapshots/run"),

  agents: (period?: string) =>
    req<{ period: string; agents: Record<string, any>[]; repeat_borrower_rate: number | null }>(
      "GET", `/api/v1/platform/analytics/agents${period ? `?period=${period}` : ""}`),

  // ---- User management ----
  staffList: () => req<{ staff: Record<string, any>[]; roles: string[] }>(
    "GET", "/api/v1/platform/users/staff"),
  createStaff: (body: Record<string, unknown>) =>
    req<Record<string, any>>("POST", "/api/v1/platform/users/staff", body),
  updateStaff: (id: string, body: Record<string, unknown>) =>
    req<Record<string, any>>("PATCH", `/api/v1/platform/users/staff/${id}`, body),
  setStaffStatus: (id: string, status: string, reason?: string) =>
    req<Record<string, any>>("POST", `/api/v1/platform/users/staff/${id}/status`, { status, reason }),
  resetStaffPassword: (id: string) =>
    req<{ temporary_password: string | null }>("POST", `/api/v1/platform/users/staff/${id}/password`, {}),
  setCustomerStatus: (id: string, status: string, reason?: string) =>
    req("POST", `/api/v1/platform/users/customers/${id}/status`, { status, reason }),

  // ---- Loan products ----
  products: () => req<{ products: Record<string, any>[]; limits: Record<string, number> }>(
    "GET", "/api/v1/platform/products"),
  updateProduct: (tier: string, body: Record<string, unknown>) =>
    req<Record<string, any>>("PUT", `/api/v1/platform/products/${tier}`, body),

  // ---- CSV exports ----
  // Server-side so the file is region-scoped and audited like the screen it
  // came from; the download is triggered from a blob because the endpoint
  // needs the bearer token a plain link cannot send.
  downloadCsv: async (dataset: string, params = "", fallbackName = "export.csv") => {
    const { blob, filename } = await requestFile(
      `/api/v1/platform/exports/${dataset}.csv${params}`);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  // ---- GOVERN ----
  auditEvents: (params = "") => req<{ total: number; events: Record<string, unknown>[] }>("GET", `/api/v1/platform/audit/events${params}`),
  regions: () => req<{ regions: Record<string, unknown>[] }>("GET", "/api/v1/platform/regions"),
  createRegion: (name: string, code: string) => req("POST", "/api/v1/platform/regions", { name, code }),
};
